import { useState, type FormEvent, useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import { brandInfo } from '@/mocks/homeData';
import { config } from '@/config';

declare global {
  interface Window {
    turnstile?: {
      render: (container: string | HTMLElement, options: Record<string, unknown>) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
    onTurnstileLoad?: () => void;
  }
}

export default function ContactSection() {
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const turnstileRef = useRef<HTMLDivElement | null>(null);
  const turnstileIdRef = useRef<string>('');
  const turnstileLoaded = useRef(false);

  // Load Turnstile script once
  useEffect(() => {
    if (!config.forms.turnstileSiteKey) return;
    if (turnstileLoaded.current) return;

    const existingScript = document.querySelector('script[src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"]');
    if (existingScript) {
      turnstileLoaded.current = true;
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=onTurnstileLoadContact';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    (window as unknown as Record<string, unknown>).onTurnstileLoadContact = () => {
      turnstileLoaded.current = true;
      if (turnstileRef.current) {
        try {
          turnstileIdRef.current = window.turnstile!.render(turnstileRef.current, {
            sitekey: config.forms.turnstileSiteKey,
            theme: 'light',
            size: 'normal',
          });
        } catch {
          // Turnstile render failed silently
        }
      }
    };
  }, []);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = formData.get('website_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setStatus('success');
      return;
    }
    formData.delete('website_alt');

    setStatus('idle');
    setErrorMsg('');

    try {
      const response = await fetch(config.forms.contactUrl, {
        method: 'POST',
        body: new URLSearchParams(formData as unknown as Record<string, string>),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });

      const responseText = await response.text();
      let parsed: { success?: boolean; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.success) {
        setStatus('success');
        form.reset();
        // Reset Turnstile
        if (turnstileIdRef.current && window.turnstile) {
          try { window.turnstile.reset(turnstileIdRef.current); } catch { /* ignore */ }
        }
      } else {
        setErrorMsg(parsed?.message || 'Submission failed. Please try again.');
        setStatus('error');
        if (turnstileIdRef.current && window.turnstile) {
          try { window.turnstile.reset(turnstileIdRef.current); } catch { /* ignore */ }
        }
      }
    } catch {
      setErrorMsg('Network error. Please check your connection and try again.');
      setStatus('error');
    }
  };

  const contactCards = [
    {
      icon: 'ri-phone-fill',
      title: 'Phone',
      value: brandInfo.phone,
      href: `tel:${brandInfo.phone}`,
      color: 'bg-primary-100 text-primary-600',
    },
    {
      icon: 'ri-map-pin-fill',
      title: 'Address',
      value: brandInfo.address,
      href: brandInfo.googleMapsUrl || 'https://maps.google.com/?q=Shop+No+2+Tumkur+Koratagere+Highway+Yallapura+Tumakuru+Karnataka+572106',
      color: 'bg-accent-100 text-accent-600',
    },
    {
      icon: 'ri-time-fill',
      title: 'Business Hours',
      value: brandInfo.hours,
      href: null,
      color: 'bg-secondary-100 text-secondary-600',
    },
  ];

  return (
    <section id="contact" className="py-16 md:py-24 bg-background-50">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="text-center mb-12 md:mb-16"
        >
          <span className="inline-block bg-primary-100 text-primary-900 px-4 py-1.5 rounded-full text-xs md:text-sm font-semibold mb-4">
            Get In Touch
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4">
            Contact Us
          </h2>
          <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto">
            Visit our store or reach out for mobiles, home appliances, TVs, DTH, money transfer, and more.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 mb-10 md:mb-14">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="space-y-4 md:space-y-5"
          >
            {contactCards.map((card, index) => (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                whileHover={{ x: 4, transition: { duration: 0.2 } }}
                className="bg-background-100 border border-background-200/70 rounded-xl p-5 md:p-6 flex items-start gap-4 hover:border-primary-300 transition-colors duration-300"
              >
                <div className={`w-11 h-11 flex items-center justify-center rounded-xl shrink-0 ${card.color}`}>
                  <i className={`${card.icon} text-lg`}></i>
                </div>
                <div>
                  <p className="font-semibold text-foreground-950 text-sm md:text-base mb-0.5">{card.title}</p>
                  {card.href ? (
                    <a href={card.href} className="text-foreground-600 text-sm md:text-base hover:text-primary-500 transition-colors cursor-pointer">
                      {card.value}
                    </a>
                  ) : (
                    <p className="text-foreground-600 text-sm md:text-base">{card.value}</p>
                  )}
                </div>
              </motion.div>
            ))}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="rounded-xl overflow-hidden border border-background-200/70"
            >
              <img
                src="https://public.readdy.ai/ai/img_res/edited_d2003fb7433521efb149210fb714dff0_951c0638.jpg"
                alt="Ashok Communication Store with Vivo Mascot"
                className="w-full h-56 md:h-64 object-cover"
              />
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            <form
              onSubmit={handleSubmit}
              className="bg-background-100 border border-background-200/70 rounded-xl p-5 md:p-6 hover:border-primary-300 transition-colors duration-300"
            >
              <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 mb-4">
                Send us a Message
              </h3>

              <div className="honeypot-field">
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    Your Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="Enter your name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="Enter your email"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="Enter your phone number"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                    placeholder="How can we help you?"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">Maximum 500 characters</p>
                </div>

                {config.forms.turnstileSiteKey && (
                  <div ref={turnstileRef} className="flex justify-center"></div>
                )}

                <button
                  type="submit"
                  className="w-full bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold py-3 rounded-lg text-sm transition-all whitespace-nowrap cursor-pointer hover:scale-[1.01] active:scale-[0.99]"
                >
                  {status === 'idle' ? 'Send Message' : status === 'success' ? 'Sent!' : 'Try Again'}
                </button>

                {status === 'success' && (
                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-sm text-accent-600 font-medium text-center"
                  >
                    Thank you! We will get back to you shortly.
                  </motion.p>
                )}

                {status === 'error' && (
                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-sm text-primary-600 font-medium text-center"
                  >
                    {errorMsg || 'Something went wrong. Please try again.'}
                  </motion.p>
                )}
              </div>
            </form>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.6 }}
          className="rounded-xl overflow-hidden border border-background-200/70"
        >
          <iframe
            src={brandInfo.googleMapsEmbed}
            width="100%"
            height="400"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Ashok Telecom Location"
            className="w-full"
          ></iframe>
        </motion.div>
      </div>
    </section>
  );
}